import { EntityApprovalStatus, entityApprovalStatus } from '@/api/entity/types';
import { type UserInvitationStatus, userInvitationStatus } from '@/api/gamma-team/types';
import type { Affiliation, UserProfileRes } from '@/api/user-dashboard/queries';
import { colors } from '@/design-system/colors';
import { UserRole } from '@/stores/slices/selected-user-config';

import type { DotsColor } from '../Dots/types';
import { SidebarLink } from '../Sidebar/use-get-sidebar-links';

export type RoleDotColor = Extract<DotsColor, 'green' | 'red' | 'purple'>;

export const ROLE_CONFIG: Record<UserRole, { label: string; color: string; dot: RoleDotColor }> = {
  admin: { label: 'Gamma Prime', color: colors.error.solid, dot: 'red' },
  user: { label: 'Personal Account', color: colors.brand.solid, dot: 'green' },
  entity: { label: 'Entity', color: colors.accent.solid, dot: 'purple' },
};

export const collectHrefs = (links: SidebarLink[][]) =>
  links
    .flat()
    .map((l) => l.href)
    .filter((h): h is string => !!h);

// View state reads the same in every cabinet; any other param names an object of the cabinet
// being left (a board card, a chat thread) that the next cabinet may not see.
const CABINET_NEUTRAL_PARAMS = ['tab', 'view', 'filter'];

const sectionOf = (path: string) => path.split(/[?#]/)[0].split('/')[1] ?? '';

/**
 * Where a cabinet switch takes the user: nowhere (`null`) while the next cabinet's sidebar has the
 * current section, its section root from a detail page, otherwise the cabinet's first link.
 */
export const getCabinetSwitchPath = (
  { pathname, search }: { pathname: string; search: string },
  hrefs: string[],
): string | null => {
  const sectionHref = hrefs.find((href) => sectionOf(href) === sectionOf(pathname));
  if (!sectionHref) return hrefs[0] ?? null;
  if (pathname !== sectionHref.split('?')[0]) return sectionHref;

  const params = new URLSearchParams(search);
  const kept = new URLSearchParams(
    [...params].filter(([key]) => CABINET_NEUTRAL_PARAMS.includes(key)),
  );
  if (kept.toString() === params.toString()) return null;

  return kept.size ? `${pathname}?${kept}` : pathname;
};

export const entityStatusLabel: Record<EntityApprovalStatus, string> = {
  [entityApprovalStatus.Verified]: 'Verified',
  [entityApprovalStatus.ReVerificationPending]: 'Re-verification Pending',
  [entityApprovalStatus.AwaitingApproval]: 'Awaiting Approval',
  [entityApprovalStatus.Blocked]: 'Blocked',
  [entityApprovalStatus.Rejected]: 'Rejected',
  [entityApprovalStatus.Draft]: 'Draft',
};

export const PENDING_INVITATION_NOTE = 'Invitation pending';
export const PENDING_INVITATION_TOOLTIP =
  'Accept the invitation in Manage Account → Entity Affiliations';

export const shouldLoadPendingInvitations = (
  user: Pick<UserProfileRes, 'isBlocked'> | undefined,
): boolean => !!user && !user.isBlocked;

export const getPendingGammaRowCopy = (
  status: UserInvitationStatus,
  missingStepsText: string,
): { note: string; tooltip: string } => {
  if (status !== userInvitationStatus.Accepted) {
    return { note: PENDING_INVITATION_NOTE, tooltip: PENDING_INVITATION_TOOLTIP };
  }

  return {
    note: 'Awaiting verification',
    tooltip: missingStepsText
      ? `Please ${missingStepsText} to join the Gamma Prime team.`
      : PENDING_INVITATION_TOOLTIP,
  };
};

export const getPendingEntityInvitations = (
  affiliations: Affiliation[] | undefined,
  entities: { entityId: string }[] | undefined,
): Affiliation[] => {
  const seen = new Set(entities?.map((entity) => entity.entityId));

  return (affiliations ?? []).filter((affiliation) => {
    const awaitsUser =
      affiliation.status === 'WaitingForApproval' && !!affiliation.entityInvitationId;
    if (!awaitsUser || seen.has(affiliation.entityId)) return false;
    seen.add(affiliation.entityId);
    return true;
  });
};
